
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { IRouteMeta } from "@/router/interface";

import unit_routes from "@/api/routes/units";

import Preloader from "@/components/Preloader.vue";
import CInfiniteScroll from "@/components/InfiniteScroll.vue";
import UnitVersion, { EUnitableType } from "@/models/unit/unit_version";
import { EAdmissionPermission } from "@/models/admission/permission";
import UnitUnit from "@/models/unit/unit";

import { IChangedTesting, IChangingTesting } from "./TestingChange.vue";
import { IChangeArchiveTesting } from "./ChangeArchiveTesting.vue";
import { IFilters } from "./Filters.vue";

interface IData {
  readonly current_page: number;
  readonly next_page: boolean;
}

type TTesting = Pick<UnitVersion, "id" | "title"> & {
  unit: Pick<UnitUnit, "is_archived">;
};

interface ITestingsData extends IData {
  readonly unit_versions: TTesting[];
}

@Component({
  name: "CTestingsList",
  components: {
    Preloader,
    CInfiniteScroll
  }
})
export default class CTestingsList extends Vue {
  @Prop({ required: true }) public filters!: IFilters;

  public preload: boolean = false;
  private preload_infinite: boolean = false;

  private per_page: number = 100;
  private current_page: number = 0;
  private next_page: boolean = false;

  public testings: TTesting[] = [];

  private search: string = this.$store.state.search;

  private watchers: Function[] = [];

  protected beforeDestroy() {
    this.watchers.forEach(unwatch => {
      unwatch();
    });
  }

  protected async created() {
    this.preload = true;

    this.watchers.push(
      this.$store.watch(
        state => state.search,
        async search => {
          this.search = search;

          this.preload = true;
          this.testings = [];

          await this.loadTestings();
        }
      )
    );

    this.watchers.push(
      this.$store.watch(
        state => state.changedTestingProgram,
        (changedTestingProgram: IChangedTesting) => {
          if (changedTestingProgram.index != null) {
            const testing = this.testings[changedTestingProgram.index];

            testing.title = changedTestingProgram.name;
          } else {
            this.testings.unshift({
              id: changedTestingProgram.id,
              title: changedTestingProgram.name,
              unit: { is_archived: false }
            });
          }
        }
      )
    );

    this.watchers.push(
      this.$store.watch(
        state => state.changedArchiveTestingProgram,
        (changedArchiveTestingProgram: IChangeArchiveTesting) => {
          const testing = this.testings[changedArchiveTestingProgram.index];

          if (testing) {
            testing.unit.is_archived = changedArchiveTestingProgram.is_archived;

            if (
              !this.filters.include_archived &&
              changedArchiveTestingProgram.is_archived
            ) {
              this.testings.splice(changedArchiveTestingProgram.index, 1);
            }
          }
        }
      )
    );

    await this.loadTestings();
  }

  @Watch("filters")
  protected async changeFilters() {
    this.preload = true;
    this.testings = [];

    await this.loadTestings();
  }

  public async infiniteHandler() {
    if (this.next_page && !this.preload_infinite) {
      await this.loadTestings(this.current_page + 1);
    }
  }

  public async archiveTesting(testing: TTesting, index: number) {
    this.$store.commit("changingArchiveTestingProgram", {
      id: testing.id,
      title: testing.title,
      is_archived: testing.unit.is_archived,
      index
    });
  }

  private async loadTestings(page: number = 0) {
    this.preload_infinite = true;

    return this.$api
      .get(unit_routes.versions, {
        params: {
          page,
          per_page: this.per_page,
          paginate: true,
          search: this.search,
          ...this.filters,
          unitable_type: EUnitableType["Test::Version"],
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: ITestingsData }) => {
        this.current_page = res.current_page;
        this.next_page = res.next_page;

        this.testings.push(...res.unit_versions);
      })
      .finally(() => {
        this.preload = false;
        this.preload_infinite = false;
      });
  }

  public createTesting() {
    const changing_testing: IChangingTesting = {
      id: null,
      index: null
    };

    this.$store.commit("changingTestingProgram", changing_testing);
  }

  public updateTesting(testing: TTesting, index: number) {
    const changing_testing: IChangingTesting = {
      id: testing.id,
      index
    };

    this.$store.commit("changingTestingProgram", changing_testing);
  }

  public allowWrite() {
    return this.$api.hasWritePermissions(
      [EAdmissionPermission.TESTINGS, EAdmissionPermission.EDUCATION_PROGRAMS],
      false
    );
  }
}
